import React from 'react'
import {Alert, Button, Col, Container, FormControl, FormGroup, FormLabel, Row, Table} from 'react-bootstrap'
import StyledSelect from "../../components/StyledSelect";
import './ReferralList.css'
import {format, parseISO} from "date-fns";
import {BsArrowClockwise, BsExclamationCircleFill, BsEye, BsSearch} from "react-icons/bs";
import ReferralDetailsDialog from './ReferralDetailsDialog';
import PaginationControls from "../../components/PaginationControls";

const statusDisplayMap = {
    'Skickad': 'Skickad, inväntar mottagning',
    'Ej bedömd': 'Mottagen, ej bedömd',
    'Bedömning pågår': 'Bedömning pågår',
    'Accepterad': 'Accepterad',
    'Pågående': 'Behandling pågår',
    'Komplettering begärd': 'Komplettering begärd',
    'Avslutad': 'Avslutad',
    'Avvisad': 'Avvisad',
    'Makulerad': 'Makulerad',
    'Återkallad': 'Återkallad',
};

const displayStatus = (status) => statusDisplayMap[status] || status;

const ReferralList = ({
                          referrals,
                          careUnits,
                          opticians,
                          searchPersonnummer,
                          refreshStatus,
                          listReferrals,
                          openReferralDetailsDialog,
                          closeDialogs,
                          selectedReferralId,
                          referralDetails,
                          detailsLoading,
                          detailsError,
                          referralContent,
                          contentLoading,
                          contentError,
                          detailsDialogOpen,
                          errorMessage,
                          userRole,
                          userOrganizationId,
                          currentPage,
                          totalPages,
                          totalElements,
                      }) => {

    const personnummerInputRef = React.createRef();
    const [selectedCareUnit, setSelectedCareUnit] = React.useState('');
    const [selectedOptician, setSelectedOptician] = React.useState('');

    const isCareUnit = userRole === 'CARE_UNIT' || userRole === 'CARE_UNIT_ADMIN';
    const isOptician = userRole === 'OPTICIAN' || userRole === 'OPTICIAN_ADMIN';

    // Initialize filters based on user role
    React.useEffect(() => {
        if (isCareUnit && userOrganizationId) {
            setSelectedCareUnit(userOrganizationId);
            listReferrals(userOrganizationId, null);
        } else if (isOptician && userOrganizationId) {
            setSelectedOptician(userOrganizationId);
            listReferrals(null, userOrganizationId);
        } else {
            // Load all referrals for ADMIN
            listReferrals(null, null);
        }
    }, [userOrganizationId, isCareUnit, isOptician]);

    // Handle filter changes (care unit or optician dropdown)
    const onCareUnitChange = (val) => {
        setSelectedCareUnit(val);
        listReferrals(val || null, selectedOptician || null);
    }

    const onOpticianChange = (val) => {
        setSelectedOptician(val);
        listReferrals(selectedCareUnit || null, val || null);
    }

    const onPageChange = (page) => {
        listReferrals(selectedCareUnit || null, selectedOptician || null, page);
    }

    // Handle personnummer search
    const onSearch = () => {
        const personnummer = personnummerInputRef.current.value;

        if (personnummer && personnummer.trim() !== '') {
            const careUnitId = selectedCareUnit || (careUnits && careUnits.length > 0 ? careUnits[0].organizationId : null);

            if (!careUnitId) {
                alert('Ingen vårdenhet tillgänglig för sökning');
                return;
            }

            searchPersonnummer(personnummer.trim(), careUnitId);
        } else {
            // If no personnummer, just list with current filters
            listReferrals(selectedCareUnit || null, selectedOptician || null);
        }
    }

    const onRefresh = (referralId) => {
        refreshStatus(referralId);
    }

    return (
        <div className="ReferralList">
            <Container>
                {errorMessage &&
                    <Row>
                        <Col xs={12} md={12} lg={12}>
                            <Alert variant={"danger"}>
                                <b><BsExclamationCircleFill/> {errorMessage}</b>
                            </Alert>
                        </Col>
                    </Row>
                }
                <Row>
                    <Col className="mb-3" xs={12} md={12} lg={12}>
                        <h1>Remisser</h1>
                    </Col>
                </Row>
                <Row>
                    <Col className="mb-3" xs={12} sm={6} md={4} lg={3}>
                        <FormGroup controlId="formCareUnit">
                            <FormLabel>Vårdenhet</FormLabel>
                            <StyledSelect
                                value={selectedCareUnit}
                                placeholder="Alla vårdenheter"
                                options={[
                                    {value: "", label: "Alla vårdenheter"},
                                    ...(careUnits || []).map(cu => ({value: cu.organizationId, label: cu.name}))
                                ]}
                                onChange={onCareUnitChange}
                                disabled={isCareUnit || isOptician}
                            />
                        </FormGroup>
                    </Col>
                    <Col className="mb-3" xs={12} sm={6} md={4} lg={3}>
                        <FormGroup controlId="formOptician">
                            <FormLabel>Optiker</FormLabel>
                            <StyledSelect
                                value={selectedOptician}
                                placeholder="Alla optiker"
                                options={[
                                    {value: "", label: "Alla optiker"},
                                    ...(opticians || []).map(o => ({value: o.organizationId, label: o.name}))
                                ]}
                                onChange={onOpticianChange}
                                disabled={isOptician}
                            />
                        </FormGroup>
                    </Col>
                    <Col className="mb-3" xs={12} sm={6} md={4} lg={3}>
                        <FormGroup controlId="formPersonnummer">
                            <FormLabel>Personnummer</FormLabel>
                            <FormControl
                                type="text"
                                name="personnummer"
                                placeholder="ÅÅÅÅMMDD-XXXX"
                                ref={personnummerInputRef}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        onSearch();
                                    }
                                }}
                            />
                        </FormGroup>
                    </Col>
                    <Col className="mb-3" xs={12} sm={4} md={2} lg={2}
                         style={{display: 'flex', alignItems: 'flex-end'}}>
                        <Button onClick={onSearch}>
                            <BsSearch/> Sök
                        </Button>
                    </Col>
                </Row>

                {referrals && totalElements > 0 && (
                    <Row className="mt-3">
                        <Col xs={12}>
                            <h2>Hittade {totalElements} remiss{totalElements !== 1 ? 'er' : ''}</h2>
                        </Col>
                    </Row>
                )}

                {referrals && referrals.length === 0 && (
                    <Row className="mt-3">
                        <Col xs={12}>
                            <p>Inga remisser hittades.</p>
                        </Col>
                    </Row>
                )}
            </Container>

            {referrals && referrals.length > 0 && (
                <Table size="sm" hover responsive>
                    <thead>
                    <tr>
                        <th>#</th>
                        <th>Skapad</th>
                        <th>Remiss-ID</th>
                        <th>COS ID</th>
                        <th>Avsändare</th>
                        <th>Mottagare</th>
                        <th>Status</th>
                        <th>Status uppdaterad</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    {
                        referrals.map((referral, index) =>
                            <tr key={referral.referralId}>
                                <td>{currentPage * 25 + index + 1}</td>
                                <td>{referral.createdAt ? format(parseISO(referral.createdAt), 'yyyy-MM-dd HH:mm') : '-'}</td>
                                <td>{referral.referralId}</td>
                                <td>{referral.returnedReferralId || '-'}</td>
                                <td>{referral.senderName}</td>
                                <td>{referral.receiverName}</td>
                                <td>
                                    {displayStatus(referral.cachedStatus)}
                                </td>
                                <td>{referral.statusCachedAt ? format(parseISO(referral.statusCachedAt), 'yyyy-MM-dd HH:mm') : '-'}</td>
                                <td style={{minWidth: '200px'}}>
                                    <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'nowrap'}}>
                                        <Button
                                            size="sm"
                                            onClick={() => onRefresh(referral.referralId)}
                                        >
                                            <BsArrowClockwise/> Uppdatera
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            onClick={() => openReferralDetailsDialog(referral.referralId)}
                                        >
                                            <BsEye/> Öppna
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        )
                    }
                    </tbody>
                </Table>
            )}

            <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
            />

            {detailsDialogOpen && (
                <ReferralDetailsDialog
                    onCloseClicked={closeDialogs}
                    referralId={selectedReferralId}
                    referralContent={referralContent}
                    contentLoading={contentLoading}
                    contentError={contentError}
                    referralDetails={referralDetails}
                    detailsLoading={detailsLoading}
                    detailsError={detailsError}
                />
            )}
        </div>
    );
}

export default ReferralList;